import { AsyncStatus } from 'lib-shared-legacy-types';
import { Company } from 'lib-shared-types-api';
import findIndex from 'lodash/findIndex';
import { handleActions } from 'redux-actions';

import { actions } from '../actions/companyModal';

export type CompanyModalState = {
  meta: {
    asyncStatus: AsyncStatus;
  };
  ui: {
    isCompanyModalShown: boolean;
    cropperOpen: boolean;
    imageURI: null;
  };
  users: Array<any>;
  company: Company | null;
  companyId: string | null;
};

const initialState: CompanyModalState = {
  meta: {
    asyncStatus: 'succeeded',
  },
  ui: {
    isCompanyModalShown: false,
    cropperOpen: false,
    imageURI: null,
  },
  company: null,
  users: [],
  companyId: null,
};

// Destructure the Company modal actions.
export const {
  companyModalShow,
  companyModalHidden,

  companyModalAddCompany,
  companyModalAddCompanySucceeded,
  companyModalAddCompanyFailed,

  companyModalGetUsers,
  companyModalGetUsersResult,

  companyModalUpdateCompany,
  companyModalUpdateCompanySucceeded,
  companyModalUpdateCompanyFailed,
  companyModalUpdateCompanyAvatarResult,

  companyModalOpenAvatarCropperFileChange,
  companyModalCloseAvatarCropper,

  companyModalGetPresignedUrl,

  companyModalGetPresignedUrlFailed,
  companyModalAvatarUploadFailed,
} = actions;

const companyModal = handleActions<CompanyModalState, any>(
  {
    // Get presigned avatar failed.
    [companyModalGetPresignedUrlFailed.toString()]: (state, action) => {
      return { ...state, meta: { ...state.meta, asyncStatus: 'failed' } };
    },
    // Upload avatar failed.
    [companyModalAvatarUploadFailed.toString()]: (state, action) => {
      return { ...state, meta: { ...state.meta, asyncStatus: 'failed' } };
    },
    // Get presigned url for upload new avatar.
    [companyModalGetPresignedUrl.toString()]: (state, action) => {
      return {
        ...state,
        ui: { ...state.ui, cropperOpen: false },
        meta: { ...state.meta, asyncStatus: 'loading' },
      };
    },
    // Handle open avatar cropper modal.
    [companyModalOpenAvatarCropperFileChange.toString()]: (state, action) => {
      return {
        ...state,
        ui: { ...state.ui, cropperOpen: true, imageURI: action.payload },
      };
    },
    // Close avatar modal cropper.
    [companyModalCloseAvatarCropper.toString()]: (state, action) => {
      return {
        ...state,
        ui: { ...state.ui, cropperOpen: false, imageURI: null },
      };
    },
    // Show company modal.
    [companyModalShow.toString()]: (state, action) => {
      return {
        ...state,
        ui: { ...state.ui, isCompanyModalShown: true },
        companyId:
          action.payload !== undefined &&
          action.payload.company !== undefined &&
          action.payload.company !== null &&
          action.payload.company.id !== undefined &&
          action.payload.company.id !== null
            ? action.payload.company.id
            : null,
        company:
          action.payload !== undefined &&
          action.payload.company !== undefined &&
          action.payload.company !== null
            ? action.payload.company
            : null,
      };
    },

    // Hide company modal.
    [companyModalHidden.toString()]: (state, action) => {
      return {
        ...state,
        ui: { ...state.ui, isCompanyModalShown: false },
        company: null,
        companyId: null,
        users: [],
      };
    },

    // Call add company.
    [companyModalAddCompany.toString()]: (state, action) => {
      return { ...state, meta: { ...state.meta, asyncStatus: 'loading' } };
    },

    // Call update company.
    [companyModalUpdateCompany.toString()]: (state, action) => {
      return { ...state, meta: { ...state.meta, asyncStatus: 'loading' } };
    },

    // Add new company result.
    [companyModalAddCompanySucceeded.toString()]: {
      next(state, action) {
        return {
          ...state,
          meta: { ...state.meta, asyncStatus: 'succeeded' },
          ui: { ...state.ui, isCompanyModalShown: false },
        };
      },
      throw(state, action) {
        return { ...state, meta: { ...state.meta, asyncStatus: 'failed' } };
      },
    },

    [companyModalAddCompanyFailed.toString()]: (state, action) => {
      return { ...state, meta: { ...state.meta, asyncStatus: 'failed' } };
    },

    // Update company succeeded.
    [companyModalUpdateCompanySucceeded.toString()]: (state, action) => {
      return {
        ...state,
        meta: { ...state.meta, asyncStatus: 'succeeded' },
        ui: { ...state.ui, isCompanyModalShown: false },
      };
    },

    // Update company failed.
    [companyModalUpdateCompanyFailed.toString()]: (state, action) => {
      return { ...state, meta: { ...state.meta, asyncStatus: 'failed' } };
    },

    // Get related users result (support recusive fetch).
    [companyModalGetUsersResult.toString()]: {
      next(state, action) {
        const users: any = Object.assign([], state.users);
        for (const user of action.payload.data.users) {
          const foundUserIndex = findIndex(users, { id: user.id } as any);
          if (foundUserIndex > -1) {
            users[foundUserIndex] = user;
          } else {
            users.push(user);
          }
        }
        return {
          ...state,
          users: users,
          meta: { ...state.meta, asyncStatus: 'succeeded' },
        };
      },
      throw(state, action) {
        return {
          ...state,
          users: [],
          meta: { ...state.meta, asyncStatus: 'failed' },
        };
      },
    },

    // Update company with new avatar result.
    [companyModalUpdateCompanyAvatarResult.toString()]: {
      next(state, action) {
        return {
          ...state,
          meta: { ...state.meta, asyncStatus: 'succeeded' },
          company: action.payload.data,
        };
      },
      throw(state, action) {
        return { ...state, meta: { ...state.meta, asyncStatus: 'failed' } };
      },
    },
  },
  initialState,
);

export default companyModal;
